import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { IMAGES, SITE } from "../siteConfig";

export default function About() {
  return (
    <>
      <section data-testid="about-hero" className="bg-forest pb-20 pt-40 text-limestone md:pb-28 md:pt-52">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal><Eyebrow light>About</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              Aviation discipline, applied to your land.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20 md:px-8 md:py-28">
        <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <h2 className="font-serif text-2xl leading-snug text-forest md:text-3xl">
                A pilot's approach to every flight.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-charcoal/85">
                Cotswold Drone Mapping is run by a former military pilot who now flies as a commercial airline
                pilot. That background brings the habits that professional aviation is built on: careful
                planning, thorough risk assessment, honest weather decisions and calm, methodical execution —
                applied to every drone flight, however small the job.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-5 text-base leading-relaxed text-charcoal/85">
                It also shapes how the outputs are made. A flight is only ever the means; the point is a clear,
                organised result that helps you understand your land, brief others and keep useful records.
              </p>
            </Reveal>
            <Reveal delay={0.26}>
              <div className="mt-8 border-l-2 border-ochre pl-5">
                <p className="font-serif text-lg italic leading-relaxed text-forest md:text-xl">
                  "Plan thoroughly. Fly conservatively. Deliver clearly."
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.32}>
              <dl className="mt-10 grid gap-6 border-t border-forest/15 pt-8 sm:grid-cols-2">
                <div>
                  <dt className="eyebrow text-forest/70">Based</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-charcoal/85">{SITE.base}</dd>
                </div>
                <div>
                  <dt className="eyebrow text-forest/70">Covering</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-charcoal/85">
                    The {SITE.areas.slice(0, -1).join(", ")} and {SITE.areas[SITE.areas.length - 1]}
                  </dd>
                </div>
              </dl>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="relative">
            <div className="absolute -right-3 -top-3 h-full w-full border border-ochre/50" aria-hidden="true" />
            <img
              src={IMAGES.stoneWall}
              alt="Dry stone wall crossing open Cotswold pasture"
              loading="lazy"
              className="relative h-[420px] w-full object-cover lg:h-[560px]"
            />
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-20 bg-forest p-10 text-limestone md:p-14">
            <h2 className="font-serif text-2xl md:text-3xl">Say hello.</h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-limestone/80">
              Tell us about your land and what you're trying to understand.
            </p>
            <Link
              to="/contact"
              data-testid="about-page-cta"
              className="group mt-7 inline-flex items-center gap-2 rounded-full bg-ochre px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-forest-deep transition-colors hover:bg-limestone"
            >
              Discuss your mapping project
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
